// Видео: ролики по правилам показа (src/region.ts), открываются в YouTube; атрибуция канала — в карточке.
// С 14.10 (§314) сервер сам не отдаёт ролики, закрытые в стране зрителя, и кладёт перед лентой полки по командам
// (избранные — первыми, по нашему набору `teams`). Своя проверка региона осталась: вреда нет.
// «Видеоблоки» в Профиле (HQ-1059) гасят ролики на других экранах; здесь человек пришёл за видео — карточки always.
import { useLocation } from 'react-router-dom';
import { useBlockTitle } from '../../api/blockTitles';
import type { VideoShelf } from '../../api/leagueTypes';
import type { VideoItem } from '../../api/types';
import { useHighlights } from '../../api/newsQueries';
import { t } from '../../i18n/i18n';
import { viewerRegion, videoAllowed } from '../../region';
import { favoriteParams, useFavorites } from '../../session/favorites';
import { ErrorView, Loading } from '../../ui/States';
import { TeamLogo } from '../../ui/TeamLogo';
import { VideoCard } from '../../ui/VideoCard';
import { Placeholder } from '../Placeholder';
import styles from './News.module.css';

export function VideosScreen() {
  const league = new URLSearchParams(useLocation().search).get('league');
  return <VideosContent league={league} withTitle />;
}

/** Ролики экрана — полки и лента, по серверному blocks_order; незнакомый ключ пропускаем. */
export function videoBlocks(screen: { blocks_order?: string[]; shelves?: VideoShelf[]; items?: VideoItem[] }, allowed: (v: VideoItem) => boolean) {
  const order = screen.blocks_order ?? ['items'];
  const shelves = order.includes('shelves')
    ? (screen.shelves ?? []).filter((s) => s.team).map((s) => ({ ...s, items: (s.items ?? []).filter(allowed) })).filter((s) => s.items.length)
    : [];
  const items = order.includes('items') ? (screen.items ?? []).filter(allowed) : [];
  return { order: order.filter((k) => (k === 'shelves' ? shelves.length : k === 'items' ? items.length : false)), shelves, items };
}

/** Список роликов — отдельным экраном, вкладкой «Видео» в новостях и вкладкой highlights нижней панели. */
export function VideosContent({ league, withTitle, path }: { league: string | null; withTitle?: boolean; path?: string }) {
  const query = useHighlights(league, path, favoriteParams(useFavorites()).teams);
  const title = useBlockTitle('highlights');
  if (query.isPending) return <Loading query={query} />;
  if (query.isError && !query.data) return <ErrorView error={query.error} onRetry={() => void query.refetch()} />;
  const region = viewerRegion();
  const { order, shelves, items } = videoBlocks(query.data!, (v) => videoAllowed(v.compliance?.region_restriction?.blocked, region));
  // подписи блоков — только когда блоков больше одного (одна лента — как раньше, без подписи)
  const titled = order.length > 1;
  return (
    <div className={styles.page}>
      {withTitle && <h1 className={styles.title}>{query.data!.league?.name ?? t('mc_videos')}</h1>}
      {!order.length && <Placeholder title={t('mc_videos_empty')} />}
      {order.map((key) => (key === 'shelves' ? (
        <section key={key} className={styles.section}>
          {titled && <h2 className={styles.blockTitle}>{title('shelves', t('tg_videos_by_team'))}</h2>}
          {shelves.map((shelf, i) => (
            <div key={`${shelf.team!.id}-${i}`} className={styles.shelf}>
              <div className={styles.sectionHeader}>
                <TeamLogo src={shelf.team!.image} code={shelf.team!.code} size={24} />
                <h3 className={styles.sectionTitle}>{shelf.team!.name}{shelf.favorite ? ' ★' : ''}</h3>
              </div>
              <div className={styles.videoRail}>{shelf.items.map((v) => <VideoCard key={v.id} item={v} always />)}</div>
            </div>
          ))}
        </section>
      ) : (
        <section key={key} className={styles.section}>
          {titled && <h2 className={styles.blockTitle}>{title('items', t('tg_videos_latest'))}</h2>}
          <div className={styles.videoGrid}>{items.map((v) => <VideoCard key={v.id} item={v} always />)}</div>
        </section>
      )))}
    </div>
  );
}
